import { isAccessDeniedError } from '@/api';
import { Button, EmptyState, MaskedValue } from '@/components';
import { PageContentCard } from '@/components/layout/PageContentCard';
import { History } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { VisibleUserLevel, VisibleUserLevelKey } from '../../types';
import { DeviceTabLoading } from '../DeviceTabStates';
import { UserLevelKeyHistoryDialog } from './UserLevelKeyHistoryDialog';

export interface UserLevelKeysCardProps {
  deviceId: string;
  keys: VisibleUserLevelKey[];
  isLoading: boolean;
  error: unknown;
}

// UserLevelKeysCard lists the masked access keys of user levels 1-3 with a history popup per
// level. Superadmin only; the parent gates it behind the superuser.userLevelKeys flag.
// SECURITY: keys are plaintext PINs, kept only in the query cache while this card is mounted.
export function UserLevelKeysCard({ deviceId, keys, isLoading, error }: UserLevelKeysCardProps) {
  const { t } = useTranslation();
  const [historyLevel, setHistoryLevel] = useState<VisibleUserLevel | null>(null);

  return (
    <PageContentCard className="w-full">
      {/* Card header */}
      <div className="mb-4">
        <h2 className="text-onsurface text-lg font-semibold">{t('devices.detail.rules.userLevelKeys.title')}</h2>
        <p className="text-muted max-w-prose text-sm">{t('devices.detail.rules.userLevelKeys.description')}</p>
      </div>

      <KeysContent isLoading={isLoading} error={error} keys={keys} onOpenHistory={setHistoryLevel} />

      <UserLevelKeyHistoryDialog
        deviceId={deviceId}
        userLevel={historyLevel}
        onClose={() => setHistoryLevel(null)}
      />
    </PageContentCard>
  );
}

interface KeysContentProps {
  isLoading: boolean;
  error: unknown;
  keys: VisibleUserLevelKey[];
  onOpenHistory: (userLevel: VisibleUserLevel) => void;
}

// KeysContent switches between loading, no-access, empty and the key rows.
function KeysContent({ isLoading, error, keys, onOpenHistory }: KeysContentProps) {
  const { t } = useTranslation();

  if (isLoading) return <DeviceTabLoading rows={3} />;
  if (isAccessDeniedError(error)) {
    return <EmptyState title={t('devices.detail.rules.userLevelKeys.accessDenied')} className="py-8" />;
  }
  if (error) return <EmptyState title={t('devices.detail.rules.userLevelKeys.error')} className="py-8" />;
  if (keys.length === 0) return <EmptyState title={t('devices.detail.rules.userLevelKeys.empty')} className="py-8" />;

  return (
    <div className="border-border divide-border flex flex-col divide-y overflow-hidden rounded-lg border">
      {keys.map((key) => (
        <UserLevelKeyRow key={key.userLevel} userLevelKey={key} onOpenHistory={onOpenHistory} />
      ))}
    </div>
  );
}

interface UserLevelKeyRowProps {
  userLevelKey: VisibleUserLevelKey;
  onOpenHistory: (userLevel: VisibleUserLevel) => void;
}

// UserLevelKeyRow shows one level with its masked key and the history button.
function UserLevelKeyRow({ userLevelKey, onOpenHistory }: UserLevelKeyRowProps) {
  const { t } = useTranslation();
  const { userLevel } = userLevelKey;

  return (
    <div className="bg-surface flex flex-wrap items-center gap-4 px-4 py-3">
      {/* Level label */}
      <div className="w-32 flex-none">
        <div className="text-onsurface text-sm font-medium">
          {t('devices.detail.rules.userLevelKeys.level', { level: userLevel })}
        </div>
      </div>
      {/* Masked key with its reveal toggle */}
      <div className="flex-1">
        <MaskedValue value={String(userLevelKey.keyValue)} variant="inline" />
      </div>
      <Button type="button" onClick={() => onOpenHistory(userLevel)}>
        <History className="size-4" />
        {t('devices.detail.rules.userLevelKeys.history')}
      </Button>
    </div>
  );
}
